import { useState, useEffect, RefObject } from "react";
import { getCachedBlob, CachedBlob } from "@/shared/lib/blob-cache-persistent";

export type FileSource = string | File;

interface UseLazyFileUrlResult {
  url: string | null;
  size: number | null;
  loading: boolean;
  error: Error | null;
  isIntersecting: boolean;
}

interface UseLazyFileUrlOptions {
  threshold?: number;
  root?: Element | null;
  rootMargin?: string;
}

export const useLazyFileLoad = (
  ref: RefObject<Element | null>,
  src: FileSource | null | undefined,
  options: UseLazyFileUrlOptions = {}
): UseLazyFileUrlResult => {
  const { threshold = 0.1, root = null, rootMargin = "0px" } = options;

  const [isIntersecting, setIsIntersecting] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, root, rootMargin }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold, root, rootMargin]);

  // Загрузка файла при видимости
  useEffect(() => {
    if (!isIntersecting || !src) {
      return;
    }

    let isCancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        let resolvedUrl: string;
        let resolvedSize: number | null = null;

        if (src instanceof File) {
          resolvedUrl = URL.createObjectURL(src);
          resolvedSize = src.size;
        } else if (
          typeof src === "string" &&
          (src.startsWith("blob:") ||
            src.startsWith("data:") ||
            src.startsWith("http://") ||
            src.startsWith("https://"))
        ) {
          resolvedUrl = src;
        } else if (typeof src === "string") {
          const cached: CachedBlob = await getCachedBlob(src);
          resolvedUrl = cached.url;
          resolvedSize = cached.blob.size;
        } else {
          throw new Error("Unsupported source type");
        }

        if (!isCancelled) {
          setUrl(resolvedUrl);
          setSize(resolvedSize);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err as Error);
          console.warn("Failed to load lazy file:", err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
      // if (src instanceof File && url) {
      //   URL.revokeObjectURL(url);
      // }
    };
  }, [isIntersecting, src]);

  return { url, size, loading, error, isIntersecting };
};
